/* Fixed mode: the ladders list and the rung editor. The list's toggle, reorder
   and delete save straight away. The editor autosaves like Content → Bundles: a
   new ladder is created the first time it's complete (every rung has media, sale
   rungs have valid prices), then every later change updates it. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../api/client';
import type { FixedLadder, FixedLadderBody } from '../../../../api/client';
import { useAutosave, type AutosaveResult } from '../../../../hooks/useAutosave';
import type { SalesCatalogue } from '../../SalesLadder/useSalesCatalogue';
import { emptyStep, toDraft, type DraftStep } from '../../SalesLadder/types';
import { isVideoItem } from '../../SalesLadder/MediaBadge';
import { Badge, Btn, Card, Icon, Loading, NumInput, Seg, Toggle, Warn } from '../kit';
import { MediaLightbox } from '../MediaLightbox';
import { MediaPicker } from '../MediaPicker';
import { ThumbStrip } from '../ThumbStrip';
import { useResolveIds } from '../resolveIds';
import { font } from '../style';
import { C } from '../tokens';
import { Framing, IconBtn } from './StagesSection';
import { ladderBody, ladderIssues, ladderName, money, stepsOf, withName } from './model';

type T = (k: string, o?: Record<string, unknown>) => string;

/** Every rung's content_id → vault map, so thumbnails load their vault directly. */
const vaultsOf = (steps: { content_vaults?: Record<string, string> }[]) => Object.assign({}, ...steps.map((s) => s.content_vaults ?? {})) as Record<string, string>;

const summary = (steps: { type: string; price: number }[], t: T) => {
  const free = steps.filter((s) => s.type !== 'sale').length;
  const sales = steps.filter((s) => s.type === 'sale');
  const ps = sales.map((s) => s.price).filter(Boolean);
  return [
    free ? t('sl.fx.nFree', { count: free }) : '',
    t('sl.fx.nSales', { count: sales.length }),
    ps.length ? `${money(ps[0])} → ${money(ps[ps.length - 1])}` : t('sl.st.noPrices'),
  ].filter(Boolean).join(' · ');
};

/* ── List ───────────────────────────────────────────────────────── */

export function LadderList({ accountId, ladders, loading, cat, onEdit, onLadders, track }: {
  accountId: string;
  ladders: FixedLadder[];
  loading: boolean;
  cat: SalesCatalogue;
  onEdit: (l: FixedLadder | null) => void;
  onLadders: (next: FixedLadder[]) => void;
  /** Run a save and report it in the page's save status. */
  track: <R>(fn: () => Promise<R>) => Promise<R>;
}) {
  const { t } = useTranslation('assistantCompact');
  // Persisted order = priority desc (the engine's default pick: top active wins).
  const ordered = useMemo(() => [...ladders].sort((a, b) => b.priority - a.priority || a.id - b.id), [ladders]);
  const firstIds = useMemo(() => ordered.flatMap((l) => l.steps.flatMap((s) => s.content_ids).slice(0, 3)), [ordered]);
  const hints = useMemo(() => vaultsOf(ladders.flatMap((l) => l.steps)), [ladders]);
  const resolving = useResolveIds(accountId, cat, firstIds, hints);

  const put = (l: FixedLadder, over: Partial<FixedLadderBody>) => {
    onLadders(ladders.map((x) => (x.id === l.id ? { ...x, ...over } : x)));
    void track(() => api.updateFixedLadder(accountId, l.id, ladderBody(l, over))).catch(() => {});
  };
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= ordered.length) return;
    const next = [...ordered];
    [next[i], next[j]] = [next[j]!, next[i]!];
    const n = next.length;
    const changed = next.map((l, k) => ({ l, p: n - k })).filter((x) => x.l.priority !== x.p);
    onLadders(ladders.map((l) => { const c = changed.find((x) => x.l.id === l.id); return c ? { ...l, priority: c.p } : l; }));
    changed.forEach((x) => void track(() => api.updateFixedLadder(accountId, x.l.id, ladderBody(x.l, { priority: x.p }))).catch(() => {}));
  };
  const remove = (l: FixedLadder) => {
    if (!window.confirm(t('sl.fx.deleteConfirm'))) return;
    onLadders(ladders.filter((x) => x.id !== l.id));
    void track(() => api.deleteFixedLadder(accountId, l.id)).catch(() => {});
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ font: font(600, 13), color: C.text }}>{t('sl.fx.title')}</span>
        <span style={{ font: font(400, 12), color: C.muted }}>{ladders.length}</span>
        <span style={{ font: font(400, 11.5), color: C.muted }}>{t('sl.fx.hint')}</span>
        <span style={{ flex: 1 }} />
        <span data-tutorial="sl-add-ladder"><Btn kind="tint" icon="plus" onClick={() => onEdit(null)}>{t('sl.fx.new')}</Btn></span>
      </div>
      <div data-tutorial="sl-ladder-list">
        {loading ? <Loading /> : (
          <Card>
            {ordered.map((l, i) => {
              const iss = ladderIssues(l.steps, t);
              const all = l.steps.flatMap((s) => s.content_ids);
              const top = i === 0 && l.is_active;
              return (
                <div key={l.id} className="cpt-row" style={{ display: 'flex', alignItems: 'center', gap: 14, minHeight: 48, padding: '6px 14px 6px 8px', opacity: l.is_active ? 1 : 0.7 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 'none' }}>
                    <IconBtn icon="chevron-up" title={t('sl.fx.moveUp')} off={!i} onClick={() => move(i, -1)} />
                    <IconBtn icon="chevron-down" title={t('sl.fx.moveDown')} off={i === ordered.length - 1} onClick={() => move(i, 1)} />
                  </div>
                  <div onClick={() => onEdit(l)} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, cursor: 'pointer' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <span style={{ font: font(500, 13), color: ladderName(l) ? C.text : C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ladderName(l) || t('sl.fx.untitled')}</span>
                      {top && <Badge>{t('sl.fx.default')}</Badge>}
                    </span>
                    <span style={{ font: font(400, 11.5), color: C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{summary(l.steps, t)}</span>
                  </div>
                  {iss.length > 0 && <span title={iss.join('\n')}><Warn>{t('sl.fx.nIssues', { count: iss.length })}</Warn></span>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 'none' }}>
                    <ThumbStrip accountId={accountId} ids={all.slice(0, 3)} byId={cat.byId} max={3} size={26} loading={resolving} />
                    <span style={{ font: font(400, 11.5), color: C.muted, whiteSpace: 'nowrap', width: 56 }}>{t('sl.nItems', { count: all.length })}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 84, flex: 'none' }}>
                    <Toggle on={l.is_active} onChange={(v) => put(l, { is_active: v })} />
                    <span style={{ font: font(400, 12), color: l.is_active ? C.text : C.muted }}>{l.is_active ? t('sl.fx.active') : t('sl.fx.off')}</span>
                  </div>
                  <Btn kind="ghost" height={26} onClick={() => onEdit(l)}>{t('sl.fx.edit')}</Btn>
                  <button type="button" title={t('sl.fx.delete')} onClick={() => remove(l)} className="cpt-hover-text" style={{ width: 26, height: 26, flex: 'none', background: 'transparent', border: 0, color: C.muted, font: font(400, 16), cursor: 'pointer' }}>×</button>
                </div>
              );
            })}
            {!ordered.length && <div style={{ padding: '12px 14px' }}><Warn>{t('sl.fx.empty')}</Warn></div>}
          </Card>
        )}
      </div>
    </div>
  );
}

/* ── Editor ─────────────────────────────────────────────────────── */

const COLS = '28px 104px 72px 72px minmax(0,1.3fr) minmax(0,1fr) 96px';

interface Draft { name: string; steps: DraftStep[] }

export function LadderEditor({ accountId, ladder, priority, cat, seedSalePrice, onValidity, onSaved, onClose, onStatus }: {
  accountId: string;
  /** null → a new ladder (created on its first valid save). */
  ladder: FixedLadder | null;
  priority: number;
  cat: SalesCatalogue;
  seedSalePrice?: number;
  onValidity?: (ok: boolean) => void;
  onSaved: (l: FixedLadder) => void;
  onClose: () => void;
  onStatus: (a: AutosaveResult) => void;
}) {
  const { t } = useTranslation('assistantCompact');
  // A ref, not state: a save queued behind the create must see the new id at once.
  const saved = useRef<FixedLadder | null>(ladder);
  const [created, setCreated] = useState(!!ladder);
  const [d, setD] = useState<Draft>(() => {
    if (ladder) return { name: ladderName(ladder), steps: ladder.steps.map(toDraft) };
    const sale = emptyStep('sale');
    if (seedSalePrice != null) { sale.price = seedSalePrice; sale.min_price = seedSalePrice; }
    return { name: '', steps: [emptyStep('teasing'), sale] };
  });
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [pick, setPick] = useState<{ uid: string; key: 'pin' | 'pv' } | null>(null);

  const issues = useMemo(() => ladderIssues(d.steps, t), [d.steps, t]);
  useEffect(() => { onValidity?.(!issues.length); }, [issues.length, onValidity]);
  const hints = useMemo(() => vaultsOf(d.steps), [d.steps]);
  const resolving = useResolveIds(accountId, cat, useMemo(() => d.steps.flatMap((s) => [...s.content_ids, ...s.preview_ids]), [d.steps]), hints);

  const auto = useAutosave<Draft>({
    value: d,
    validate: () => (issues.length ? t('sl.fx.notSaved', { count: issues.length }) : null),
    save: async (v) => {
      const cur = saved.current;
      const body: FixedLadderBody = {
        priority: cur?.priority ?? priority,
        usage_notes: withName(cur?.usage_notes ?? null, v.name.trim()),
        is_active: cur?.is_active ?? true,
        steps: stepsOf(v.steps),
      };
      const r = cur ? await api.updateFixedLadder(accountId, cur.id, body) : await api.createFixedLadder(accountId, body);
      saved.current = r;
      setCreated(true);
      onSaved(r);
    },
  });
  useEffect(() => { onStatus(auto); }, [auto.status, auto.error]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (uid: string, p: Partial<DraftStep>) => setD((x) => ({ ...x, steps: x.steps.map((s) => (s.uid === uid ? { ...s, ...p } : s)) }));
  const move = (i: number, dir: -1 | 1) => setD((x) => {
    const j = i + dir;
    if (j < 0 || j >= x.steps.length) return x;
    const n = [...x.steps];
    [n[i], n[j]] = [n[j]!, n[i]!];
    return { ...x, steps: n };
  });
  const close = () => { auto.flush(); onClose(); };
  const target = pick ? d.steps.find((s) => s.uid === pick.uid) : undefined;
  const refOf = (i: number) => t(d.steps[i]!.type === 'sale' ? 'sl.saleN' : 'sl.teasingN', { n: d.steps.slice(0, i + 1).filter((s) => s.type === d.steps[i]!.type).length });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Btn kind="muted" icon="arrow-left" onClick={close}>{t('sl.fx.title')}</Btn>
        <input value={d.name} onChange={(e) => setD((x) => ({ ...x, name: e.target.value }))} placeholder={t('sl.fx.namePh')} className="cpt-hover-line" style={{ width: 240, maxWidth: '100%', height: 28, boxSizing: 'border-box', background: 'transparent', border: '1px solid transparent', borderRadius: 6, padding: '0 8px', color: C.text, font: font(600, 13), outline: 'none' }} />
        <span style={{ flex: 1 }} />
        {issues.length > 0 && <Warn>{t('sl.fx.nIssuesFix', { count: issues.length })}</Warn>}
        <span data-tutorial="sl-save"><Btn kind="primary" onClick={close}>{t('sl.fx.done')}</Btn></span>
      </div>
      <div data-tutorial="sl-rungs">
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, padding: '7px 14px', font: font(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>
            <span />
            <span>{t('sl.fx.colRung')}</span>
            <span>{t('sl.fx.colPrice')}</span>
            <span>{t('sl.fx.colFloor')}</span>
            <span>{t('sl.fx.colMedia')}</span>
            <span>{t('sl.fx.colPreviews')}</span>
            <span />
          </div>
          {d.steps.map((s, i) => {
            const sale = s.type === 'sale';
            const o = sale && open.has(s.uid);
            const fr = s.offer_framing ?? '';
            return (
              <div key={s.uid} style={{ background: o ? 'var(--om-panel)' : 'transparent' }}>
                <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 12, alignItems: 'center', padding: '8px 14px' }}>
                  <span style={{ width: 22, height: 22, borderRadius: 6, background: sale ? C.tint20 : C.hover, color: sale ? C.purplePale : C.sub, font: font(600, 11), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                  <div style={{ justifySelf: 'start' }}>
                    <Seg value={s.type} onChange={(v) => patch(s.uid, { type: v })} options={[{ id: 'teasing', label: t('sl.free') }, { id: 'sale', label: t('sl.fx.sale') }]} />
                  </div>
                  {sale ? (
                    <>
                      <NumInput value={s.price || null} onChange={(v) => patch(s.uid, { price: v ?? 0 })} min={0} max={99999} allowEmpty placeholder="0.00" width="100%" />
                      <NumInput value={s.min_price || null} onChange={(v) => patch(s.uid, { min_price: v ?? 0 })} min={0} max={99999} allowEmpty placeholder="0.00" width="100%" />
                    </>
                  ) : <span style={{ gridColumn: 'span 2', font: font(500, 12), color: C.purplePale, paddingLeft: 8 }}>{t('sl.free')}</span>}
                  <span data-tutorial={i === 0 ? 'sl-addmedia' : undefined} style={{ minWidth: 0 }}>
                    <Slot accountId={accountId} cat={cat} resolving={resolving} ids={s.content_ids} pin icon="layers" none={t('sl.fx.nonePinned')} tip={t('sl.fx.mediaTip')} onOpen={() => setPick({ uid: s.uid, key: 'pin' })} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    {sale
                      ? <Slot accountId={accountId} cat={cat} resolving={resolving} ids={s.preview_ids} icon="eye" none={t('none')} tip={t('sl.fx.previewTip')} onOpen={() => setPick({ uid: s.uid, key: 'pv' })} />
                      : <span style={{ font: font(400, 12), color: C.faint, paddingLeft: 2 }}>—</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    {sale && <IconBtn icon="message-square" title={t('sl.framing')} active={o || !!fr.trim()} onClick={() => setOpen((x) => { const n = new Set(x); if (n.has(s.uid)) n.delete(s.uid); else n.add(s.uid); return n; })} />}
                    <IconBtn icon="arrow-up" title={t('sl.moveUp')} off={!i} onClick={() => move(i, -1)} />
                    <IconBtn icon="arrow-down" title={t('sl.moveDown')} off={i === d.steps.length - 1} onClick={() => move(i, 1)} />
                    <IconBtn icon="trash-2" title={t('sl.fx.deleteRung')} onClick={() => setD((x) => ({ ...x, steps: x.steps.filter((z) => z.uid !== s.uid) }))} />
                  </div>
                </div>
                {o && (
                  <div style={{ padding: '0 14px 12px 54px' }}>
                    <Framing value={fr} onChange={(v) => patch(s.uid, { offer_framing: v })} />
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px' }}>
            <Btn kind="ghost" icon="plus" onClick={() => setD((x) => ({ ...x, steps: [...x.steps, emptyStep('teasing')] }))}>{t('sl.fx.addFree')}</Btn>
            <span data-tutorial="sl-addsale"><Btn kind="tint" icon="plus" onClick={() => setD((x) => ({ ...x, steps: [...x.steps, emptyStep('sale')] }))}>{t('sl.fx.addSale')}</Btn></span>
          </div>
        </Card>
      </div>
      {issues.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '2px 2px 0' }}>
          {issues.map((x) => <Warn key={x}>{x}</Warn>)}
        </div>
      )}
      {!created && <span style={{ font: font(400, 11.5), color: C.muted }}>{t('sl.fx.newNote')}</span>}
      {pick && target && (
        <MediaPicker
          accountId={accountId}
          title={t(pick.key === 'pin' ? 'sl.fx.pickMedia' : 'sl.fx.pickPreviews', { ref: refOf(d.steps.indexOf(target)) })}
          initialSelected={pick.key === 'pin' ? target.content_ids : target.preview_ids}
          vaultHints={hints}
          catalogue={cat}
          excludeIds={pick.key === 'pv' ? target.content_ids : undefined}
          allowEmpty
          onConfirm={(ids, items) => {
            // Remember each item's vault (pack and previews) so the ladder can
            // lazy-load exactly those later instead of scanning every vault.
            const pin = pick.key === 'pin';
            const vaults: Record<string, string> = {};
            for (const id of [...ids, ...(pin ? target.preview_ids : target.content_ids)]) {
              const v = items.find((it) => it.id === id)?.vault_id ?? target.content_vaults[id];
              if (v) vaults[id] = v;
            }
            patch(target.uid, pin ? { content_ids: ids, content_vaults: vaults } : { preview_ids: ids, content_vaults: vaults });
            setPick(null);
          }}
          onClose={() => setPick(null)}
        />
      )}
    </div>
  );
}

function Slot({ accountId, cat, resolving, ids, pin, icon, none, tip, onOpen }: { accountId: string; cat: SalesCatalogue; resolving: boolean; ids: string[]; pin?: boolean; icon: string; none: string; tip: string; onOpen: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const [view, setView] = useState<number | null>(null);
  const known = ids.map((id) => cat.byId.get(id)).filter((x): x is NonNullable<typeof x> => !!x);
  const v = known.filter((i) => isVideoItem(i.type)).length;
  const mix = [known.length - v ? t('picker.photos', { count: known.length - v }) : '', v ? t('picker.videos', { count: v }) : ''].filter(Boolean).join(' · ');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      {ids.length ? (
        <>
          <ThumbStrip accountId={accountId} ids={ids} byId={cat.byId} max={2} size={28} loading={resolving} onClick={onOpen} onItem={(id) => setView(known.findIndex((k) => k.id === id))} title={mix || undefined} />
          <span title={mix} style={{ flex: 1, minWidth: 0, font: font(400, 11.5), color: C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ids.length > 2 ? '' : t('sl.nItems', { count: ids.length })}</span>
        </>
      ) : <span style={{ flex: 1, minWidth: 0, font: font(400, 12), color: pin ? C.purplePale : C.muted, whiteSpace: 'nowrap' }}>{none}</span>}
      <button type="button" title={tip} onClick={onOpen} className="cpt-hover" style={{ height: 26, flex: 'none', display: 'flex', alignItems: 'center', gap: 5, padding: '0 8px', background: 'transparent', border: `1px solid ${pin && !ids.length ? 'color-mix(in srgb, var(--om-accent) 60%, transparent)' : C.borderHi}`, borderRadius: 6, color: C.purplePale, font: font(500, 12), cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <Icon name={ids.length ? 'edit-2' : icon} size={12} />{ids.length ? t('sl.fx.editSlot') : t('sl.fx.add')}
      </button>
      {view !== null && view >= 0 && <MediaLightbox accountId={accountId} items={known} start={view} onClose={() => setView(null)} />}
    </div>
  );
}
